/**
 * 把扁平消息列表按 user → assistant+toolCall → user 的 turn 边界切成 turn 序列，
 * 并为每个 turn 产出：
 *   - userMsg  : 用户问题（原始 message 对象）
 *   - aiMsgs   : 本轮所有 assistant 消息（含中间叙述 + 最终输出）
 *   - toolMsgs : 本轮所有 toolCall/tool 消息
 *   - key      : 稳定 key = 'turn-{userMsg.id}'，用于 v-for + 滚动定位锚点
 *   - question : 用户问题摘要（去 markdown + 截断 200 字）
 *   - answer   : AI 回答摘要（优先最终 assistant.text，fallback intent / toolName，去 markdown + 截断 400 字）
 *
 * 逻辑直接移植自客户端 apps/electron/src/renderer/components/chat/MessageRail.tsx 的
 * buildRailNodes + TurnCard turn 分组 —— 唯一差异是客户端 Turn 有显式 type 字段，
 * 这里我们根据 message.type 自行切分。
 */

import { getToolDisplayName } from './toolNames'

/**
 * 把 markdown / 富文本消息压成一段纯文本，用于刻度尺 hover 预览卡。
 * 直接移植客户端 plainPreview（L30-L49）。
 */
export function plainPreview(input, max = 200) {
  if (!input) return ''
  const text = String(input)
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[*+-]\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/[*_~]/g, '')
    .replace(/\r?\n/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .trim()
  if (text.length <= max) return text
  let cut = text.slice(0, max)
  const space = cut.lastIndexOf(' ')
  if (space > Math.floor(max * 0.5)) cut = cut.slice(0, space)
  return cut.trimEnd() + '…'
}

/**
 * assistant 消息的摘要生成：优先 text，其次 intent，再其次首个 tool 的 intent / toolName。
 * 移植客户端 assistantSummaryText（L58-L70）。
 */
function assistantSummary(aiMsg, toolMsgs) {
  if (!aiMsg) return ''
  if (aiMsg.content) return aiMsg.content
  // 兜底：从 toolCall 里挑第一个非 error 的
  if (toolMsgs && toolMsgs.length) {
    const firstOk = toolMsgs.find(t => !t.isError)
    if (firstOk) {
      const args = firstOk.args
      if (args) {
        try {
          const obj = typeof args === 'string' ? JSON.parse(args) : args
          if (obj && obj.intent) return String(obj.intent)
        } catch (e) { /* 解析失败，跳过 */ }
      }
      const name = getToolDisplayName(firstOk) || firstOk.toolName || ''
      if (name) return name
    }
  }
  return ''
}

/**
 * 扁平消息 → turn 序列。
 *
 * 规则：
 *   - 遇到 user 消息 → 开启新 turn（同时前一个 turn 收尾）
 *   - assistant / toolCall / tool / thinking → 归入当前 turn
 *   - 遇到 system / error 等其它类型 → 归入当前 turn 但不产出摘要
 *   - 开头可能有 system / welcome → 跳过，直到遇到第一条 user
 *
 * @param {Array} messages 扁平消息数组（通常是 store 里 cosmoClaw.messages）
 * @returns {Array<Turn>}
 */
export function buildTurns(messages) {
  if (!Array.isArray(messages) || !messages.length) return []

  const turns = []
  let current = null

  for (const m of messages) {
    if (!m) continue
    const type = m.type

    if (type === 'user') {
      // 收尾上一个 turn
      if (current) {
        turns.push(current)
      }
      current = {
        userMsg: m,
        aiMsgs: [],
        toolMsgs: [],
        key: 'turn-' + (m.id || ('u-' + turns.length))
      }
      continue
    }

    // 跳过 turn 开启前的 system / welcome 等
    if (!current) continue

    if (type === 'assistant' || type === 'thinking') {
      current.aiMsgs.push(m)
    } else if (type === 'toolCall' || type === 'tool') {
      current.toolMsgs.push(m)
    }
    // 其它（system / error / seat-limit / quota 等）也归入当前 turn，保持顺序
  }

  // 收尾最后一个
  if (current) {
    turns.push(current)
  }

  // 给每个 turn 算摘要
  for (const turn of turns) {
    const question = plainPreview(turn.userMsg.content, 200)

    // AI 回答摘要：优先最后一条 assistant 的 content（通常是最终输出）
    const lastAi = [...turn.aiMsgs].reverse().find(m => m.type === 'assistant' && m.content)
    let answer = ''
    if (lastAi) {
      answer = plainPreview(lastAi.content, 400)
    } else {
      // 没有 assistant 正文 → 用 summary
      answer = plainPreview(assistantSummary(null, turn.toolMsgs), 400)
    }
    // 如果 summary 太短，且有工具调用 → 加上工具名提示
    if (!answer && turn.toolMsgs.length) {
      answer = `使用了 ${turn.toolMsgs.length} 个工具`
    }

    turn.question = question
    turn.answer = answer
    // 这一轮有 toolCall 吗？用于决定 TurnDetail 是否可展开
    turn.hasTools = turn.toolMsgs.length > 0
  }

  return turns
}
